import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@components/Button/Button";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PortService, PortServiceDocument } from "@services/port-services/types";
import { checkPermission } from "@utils/user-permissions";
import { t } from "i18next";
import CallAdditionalDocumentsCard from "../../VesselCallDetails/CallAdditionalDocumentsCard";
import UploadDocumentsButton from "../components/UploadDocumentButton";
import { useDownloadPortServiceDocument } from "../hooks/useDownloadPortServiceDocument";
import { useRenewNOC } from "../hooks/useRenewNOC";
import { STATUS_KEYS } from "./statusKeys";

interface DocumentsProps {
  portServiceDetails: PortService;
  openUploadDocumentDrawer: (drawerInfo: PortService) => void;
  setShouldRefetch?: (arg: boolean) => void;
}
const service = "bff-vessel-call";
const LANG_BASE = "vessel-call.port-services.preview";
const DOCUMENT_TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
  CERTIFICATE: { key: "certificate", fallback: "Certificate" },
  PERMIT: { key: "permit", fallback: "Permit" },
  DOCUMENT: { key: "document", fallback: "Document" },
  INSURANCE: { key: "insurance", fallback: "Insurance" },
  PRE_SAILING_MASTER_DECLARATION: {
    key: "pre-sailing-master-declaration",
    fallback: "Pre-Sailing Master Declaration",
  },
  VESSEL_MASTER_SOF: { key: "vessel-master-sof", fallback: "Vessel Master-SoF" },
  OTHER_SPECIFY: { key: "other-specify", fallback: "Others-Specify" },
};

const NO_DOC_UPLOAD_STATUSES = [STATUS_KEYS.CANCELLED, STATUS_KEYS.COMPLETED, STATUS_KEYS.REJECTED];

type NocDocumentEntry = {
  doc: PortServiceDocument;
  originalIndex: number;
};

const isNocDocument = (document: PortServiceDocument) => !document.type || document.type === "NOC";

const translateDocumentType = (documentType?: string) => {
  if (!documentType) return documentType;

  const labelConfig = DOCUMENT_TYPE_LABELS[documentType];
  if (!labelConfig) return documentType;

  const translationKey = `vessel-call.port-services.document-types.${labelConfig.key}`;
  const translated = t(translationKey);

  return translated === translationKey ? labelConfig.fallback : translated;
};

const Documents: React.FC<DocumentsProps> = ({
  portServiceDetails,
  openUploadDocumentDrawer,
  setShouldRefetch,
}) => {
  const { userActionPermissions } = useAuthStore();
  const { handleDownload: downloadDocument, handlePreview: previewDocument } =
    useDownloadPortServiceDocument();
  const { onRenewNOC, isRenewing } = useRenewNOC({
    portServiceId: portServiceDetails.id,
    setShouldRefetch,
  });

  const normalizeDocumentDate = (value?: string | Date | null) => {
    if (!value) return undefined;
    return value instanceof Date ? value.toISOString() : value;
  };

  const getDocumentTime = (value?: string | Date | null) => {
    const normalizedDate = normalizeDocumentDate(value);
    if (!normalizedDate) return undefined;

    const time = new Date(normalizedDate).getTime();
    return Number.isNaN(time) ? undefined : time;
  };

  const getNocIssuedTime = (document: PortServiceDocument) =>
    getDocumentTime(document.issueDate) ??
    getDocumentTime(document.inspectionDate) ??
    getDocumentTime(document.expirationDate);

  const getDocumentStartDate = (document: PortServiceDocument) => {
    if (document.issueDate) {
      return {
        value: normalizeDocumentDate(document.issueDate),
        translate: `${t(`${LANG_BASE}.sire-report-issue-date`)}: `,
      };
    }

    if (document.inspectionDate) {
      return {
        value: normalizeDocumentDate(document.inspectionDate),
        translate: `${t(`${LANG_BASE}.hose-inspection-date`)}: `,
      };
    }

    return undefined;
  };

  // Helper function to find a document by ID across all document groups
  const findDocumentById = (documentId: string): PortServiceDocument | undefined => {
    if (!portServiceDetails?.documents) return undefined;

    const doc = portServiceDetails.documents.find((d) => d.id === documentId);
    if (doc) return doc;
    return undefined;
  };

  const isValidUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return /^https?:\/\//.test(url);
  };
  const handleDownload = (documentId: string | undefined) => {
    const document = findDocumentById(documentId ?? "");
    if (document?.url && isValidUrl(document.url)) {
      downloadDocument(service, document.url, document.name);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentId, document?.url);
    }
  };

  const handlePreview = (documentId: string | undefined) => {
    const document = findDocumentById(documentId ?? "");
    if (document?.url && isValidUrl(document.url)) {
      previewDocument(service, document.url);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentId, document?.url);
    }
  };

  const isButtonVisible = !NO_DOC_UPLOAD_STATUSES.includes(portServiceDetails.status);

  const documents = portServiceDetails?.documents ?? [];
  const nocDocs = documents
    .map<NocDocumentEntry>((doc, originalIndex) => ({ doc, originalIndex }))
    .filter(({ doc }) => isNocDocument(doc))
    .sort((a, b) => {
      if (
        a.doc.nocReferenceNumber !== undefined &&
        b.doc.nocReferenceNumber !== undefined &&
        a.doc.nocReferenceNumber !== b.doc.nocReferenceNumber
      ) {
        return b.doc.nocReferenceNumber - a.doc.nocReferenceNumber;
      }

      const aIssuedTime = getNocIssuedTime(a.doc);
      const bIssuedTime = getNocIssuedTime(b.doc);

      if (aIssuedTime !== bIssuedTime) {
        return (
          (bIssuedTime ?? Number.NEGATIVE_INFINITY) - (aIssuedTime ?? Number.NEGATIVE_INFINITY)
        );
      }

      return b.originalIndex - a.originalIndex;
    });
  const latestNoc = nocDocs[0];
  const pastNocs = nocDocs.slice(1);
  const showRenewNoc =
    latestNoc?.doc.renewalAllowed === true && checkPermission(userActionPermissions, "canRenewNoc");

  const isNocExpired = (doc: PortServiceDocument) => {
    const expirationTime = getDocumentTime(doc.expirationDate);
    return expirationTime !== undefined && expirationTime < Date.now();
  };

  const getNocRequestType = (document: PortServiceDocument) => {
    if (document.nocRequestType === "New Request") {
      return t(`${LANG_BASE}.new-request`);
    }

    if (document.nocRequestType === "Continuation") {
      return t(`${LANG_BASE}.continuation`);
    }

    return document.nocRequestType;
  };

  const renderRenewNocButton = () => (
    <Button
      variant="contained"
      startIcon={
        isRenewing ? <CircularProgress size={16} color="inherit" /> : <ContentPasteGoIcon />
      }
      label={t(`${LANG_BASE}.renew-noc`)}
      className="w-fit"
      sx={{ textTransform: "none" }}
      onClick={() => onRenewNOC()}
      disabled={isRenewing}
    />
  );

  const renderNocCard = (entry: NocDocumentEntry, includeRenewAction = false) => {
    const { doc } = entry;
    const documentStartDate = getDocumentStartDate(doc);
    const isExpired = isNocExpired(doc);
    const nocRequestType = getNocRequestType(doc);

    return (
      <DocumentCard
        key={doc.id ?? `${doc.name}-${entry.originalIndex}`}
        title={
          <>
            <span>{t(`${LANG_BASE}.noc`)}</span>
            {nocRequestType && <span className="font-normal"> - {nocRequestType}</span>}
          </>
        }
        fileName={doc.name}
        startDate={documentStartDate?.value}
        startDateTrans={documentStartDate?.translate}
        expiryDate={normalizeDocumentDate(doc.expirationDate)}
        onDownload={() => {
          handleDownload(doc.id);
        }}
        onPreview={() => {
          handlePreview(doc.id);
        }}
        nocStatus={isExpired ? t(`${LANG_BASE}.expired`) : t(`${LANG_BASE}.active`)}
        nocStatusTrans={t(`${LANG_BASE}.noc-status`)}
        includeTime={true}
        rootClassName={isExpired ? "bg-[#FCE7EA]" : undefined}
        action={includeRenewAction && showRenewNoc ? renderRenewNocButton() : undefined}
      />
    );
  };

  return (
    <>
      {isButtonVisible && (
        <div className="flex justify-end">
          <UploadDocumentsButton
            portServiceDetails={portServiceDetails}
            openUploadDocumentDrawer={openUploadDocumentDrawer}
          />
        </div>
      )}
      {latestNoc && <div className="grid grid-cols-2 gap-6">{renderNocCard(latestNoc, true)}</div>}
      {pastNocs.length > 0 && (
        <>
          <p className="text-sm font-medium text-[#646A77]">{t(`${LANG_BASE}.past-nocs`)}</p>
          <div className="grid grid-cols-2 gap-6">
            {pastNocs.map((entry) => renderNocCard(entry))}
          </div>
        </>
      )}
      {portServiceDetails?.documents?.some((doc) => doc.type && doc.type !== "NOC") && (
        <>
          {nocDocs.length > 0 && (
            <p className="text-sm font-medium text-[#646A77]">{t(`${LANG_BASE}.documents`)}</p>
          )}
          <div className="grid grid-cols-2 gap-6">
            {portServiceDetails.documents
              .filter((doc) => doc.type && doc.type !== "NOC" && doc.type !== "ADDITIONAL_DOCUMENT")
              .map((doc) => {
                const documentStartDate = getDocumentStartDate(doc);
                return (
                  <DocumentCard
                    key={doc.id}
                    title={doc.name}
                    fileName={doc.name}
                    startDate={documentStartDate?.value}
                    startDateTrans={normalizeDocumentDate(doc.issueDate)}
                    expiryDate={normalizeDocumentDate(doc.expirationDate)}
                    documentTypeTrans={t(`${LANG_BASE}.document-type`)}
                    documentType={translateDocumentType(doc.documentType)}
                    documentSummaryTrans={t(`${LANG_BASE}.document-summary`)}
                    documentSummary={doc.documentSummary}
                    onDownload={() => {
                      handleDownload(doc.id);
                    }}
                    onPreview={() => {
                      handlePreview(doc.id);
                    }}
                  />
                );
              })}
            {portServiceDetails.documents
              .filter((doc) => doc.type && doc.type === "ADDITIONAL_DOCUMENT")
              .map(
                (
                  {
                    id,
                    name,
                    fileName,
                    originalFileName,
                    issueDate,
                    expirationDate,
                    specify,
                    documentType,
                    documentSummary,
                  },
                  idx,
                ) => {
                  return (
                    <div key={`${name}-${idx}`} className="w-full">
                      <CallAdditionalDocumentsCard
                        testId={`Port-service--additional-document-${idx}`}
                        onDownload={() => {
                          handleDownload(id);
                        }}
                        onPreview={() => {
                          handlePreview(id);
                        }}
                        fileName={fileName || originalFileName}
                        issueDate={normalizeDocumentDate(issueDate)}
                        expiryDate={normalizeDocumentDate(expirationDate)}
                        documentType={documentType}
                        otherSpecifyType={specify}
                        documentSummary={documentSummary}
                        documentName={name}
                      />
                    </div>
                  );
                },
              )}
          </div>
        </>
      )}
    </>
  );
};

export default Documents;
